import React, { useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Tracking } from '@iwoca/frontend-tracking-library';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import TagManager from 'react-gtm-module';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import { LoadingSpinner } from 'components/LoadingSpinner/LoadingSpinner';
import { ProductsLoader } from 'components/ProductsLoader/ProductsLoader';
import { IntroducerContextProvider } from 'contexts/IntroducerContext/IntroducerContext';

import 'styles/index.css';
import 'applicationTable/Table.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';

import { isIwocaDE } from 'utils/countryHelper';
import { DE_LOCALE, UK_LOCALE } from 'utils/i18n';
import { oneTrustCookies } from 'utils/oneTrustCookies';
import getRuntimeEnvironment from 'utils/runtimeEnvironment';

const isProductionBuild =
  process.env.NODE_ENV === 'production' && !process.env.STORYBOOK_BUILD;

if (isProductionBuild) {
  datadogLogs.init({
    clientToken:
      getRuntimeEnvironment() === 'production'
        ? 'pubd4d44f118d22163983e204126e748dfd'
        : 'puba4c457086288b431150f843896d21939',
    site: 'datadoghq.com',
    service: 'introducer_portal',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env: getRuntimeEnvironment(),
    version: process.env.REACT_APP_RELEASE,
  });

  datadogRum.init({
    applicationId:
      getRuntimeEnvironment() === 'production'
        ? 'aba9cda9-8804-46fc-8827-f5c4608c7acc'
        : '53155a74-e1a4-4098-8ace-0f2cc58e7706',
    clientToken:
      getRuntimeEnvironment() === 'production'
        ? 'pubf1e7d967f260e8d19b3d75368ec583fb'
        : 'pub9836fd2e625754d82b9da10ef17562a3',
    site: 'datadoghq.com',
    service: 'introducer-portal',
    version: process.env.REACT_APP_RELEASE,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});
Tracking.initialize({
  customerJourney: 'Introducer portal',
  trackWithDataAttribute: true,
});

if (typeof window !== 'undefined') {
  Tracking.amplitudeInitialize();
}

function MyApp({ Component, pageProps }: AppProps) {
  const locale = isIwocaDE() ? DE_LOCALE : UK_LOCALE;
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(true);
  const oneTrustDomainScript = oneTrustCookies(getRuntimeEnvironment(), locale);

  // Start Google Tag Manager, Full Story gets generated from this too
  useEffect(() => {
    if (getRuntimeEnvironment() !== 'production') return;

    function getGTMID() {
      const host = window.location.host;
      const isDE = host && host.indexOf('.de') !== -1;
      if (isDE) return 'GTM-PN3DHT';
      return 'GTM-KH9323';
    }

    TagManager.initialize({ gtmId: getGTMID() });
  }, []);

  useEffect(() => {
    async function load(locale: any) {
      let data;
      if (process.env.NODE_ENV === 'production') {
        data = await import(`../locales/${locale}/messages.js`);
      } else {
        data = await import(
          `@lingui/loader!../locales/${locale}/messages.json?raw-lingui`
        );
      }

      i18n.load(locale, data.messages);
      i18n.activate(locale);

      setIsLoadingTranslations(false);
    }

    load(locale);
  }, [locale]);

  return isLoadingTranslations ? (
    <LoadingSpinner />
  ) : (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <Script
        src={`https://cdn-ukwest.onetrust.com/consent/${oneTrustDomainScript}/OtAutoBlock.js`}
        type="text/javascript"
      />
      <Script
        src="https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js"
        type="text/javascript"
        data-domain-script={`${oneTrustDomainScript}`}
      />
      <Script
        id="onetrust-script"
        type="text/javascript"
        onLoad={() => {
          function OptanonWrapper() {}
          OptanonWrapper();
        }}
      />
      <I18nProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary locale={i18n.locale}>
            <IntroducerContextProvider>
              <ProductsLoader>
                <Component {...pageProps} />
              </ProductsLoader>
            </IntroducerContextProvider>
          </ErrorBoundary>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </I18nProvider>
    </>
  );
}

export default MyApp;
